import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import cytoscape from 'cytoscape';

const API_URL = import.meta.env.VITE_API_URL || '';

interface GraphLink {
  s: string;
  t: string;
  link_type: string;
  s_eo: string;
  s_t: string;
  t_eo: string;
  t_t: string;
  base: string;
  confidence?: number;
}

export const GraphPage: React.FC = () => {
  const cyRef = useRef<HTMLDivElement>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchId, setSearchId] = useState('');
  const [stats, setStats] = useState({ crm: 0, actual: 0, cross: 0, nodes: 0, totalLinks: 0 });
  const cyInstance = useRef<cytoscape.Core | null>(null);

  useEffect(() => {
    loadAll();
    return () => {
      if (cyInstance.current) {
        cyInstance.current.destroy();
        cyInstance.current = null;
      }
    };
  }, []);

  const buildElements = (links: GraphLink[]) => {
    const nodes = new Map<string, any>();
    const edges: any[] = [];

    links.forEach((l, i) => {
      const sid = l.base === 'actual' ? 'a-' + l.s : l.s;
      const tid = l.base === 'actual' ? 'a-' + l.t : l.base === 'cross' ? l.t : l.t;
      const sb = l.base === 'cross' ? 'crm' : l.base;
      const tb = l.base === 'cross' ? 'actual' : l.base;

      if (!nodes.has(sid)) {
        nodes.set(sid, {
          data: {
            id: sid,
            label: (l.s_eo || l.s).substring(0, 16),
            fullTitle: l.s_t || '',
            base: sb,
            color: sb === 'crm' ? '#3b82f6' : '#10b981',
            size: sb === 'crm' ? 45 : 35
          }
        });
      }
      if (!nodes.has(tid)) {
        nodes.set(tid, {
          data: {
            id: tid,
            label: (l.t_eo || l.t).substring(0, 16),
            fullTitle: l.t_t || '',
            base: tb,
            color: tb === 'crm' ? '#3b82f6' : '#10b981',
            size: tb === 'crm' ? 45 : 35
          }
        });
      }

      edges.push({
        data: {
          id: 'e' + i,
          source: sid,
          target: tid,
          label: l.link_type + (l.confidence ? ` ${Math.round(l.confidence*100)}%` : ''),
          edgeType: l.base,
          confidence: l.confidence || 1
        }
      });
    });

    return [...Array.from(nodes.values()), ...edges];
  };

  const initCy = (elements: any[]) => {
    if (!cyRef.current) return;
    if (cyInstance.current) {
      cyInstance.current.destroy();
      cyInstance.current = null;
    }

    console.log('Init cytoscape with', elements.length, 'elements');

    try {
      cyInstance.current = cytoscape({
        container: cyRef.current,
        elements,
        style: [
          {
            selector: 'node',
            style: {
              'background-color': 'data(color)',
              'label': 'data(label)',
              'width': 'data(size)',
              'height': 'data(size)',
              'font-size': '10px',
              'text-valign': 'center',
              'text-halign': 'center',
              'color': '#ffffff',
              'text-wrap': 'wrap',
              'text-max-width': '50px',
              'border-width': 2,
              'border-color': '#ffffff55',
              'border-opacity': 1,
              'background-opacity': 0.9
            }
          },
          {
            selector: 'edge[edgeType="crm"]',
            style: { 
              'line-color': '#64748b', 
              'target-arrow-color': '#64748b', 
              'width': 1,
              'line-opacity': 0.6
            }
          },
          {
            selector: 'edge[edgeType="actual"]',
            style: { 
              'line-color': '#8b5cf6', 
              'target-arrow-color': '#8b5cf6', 
              'width': 1.5, 
              'line-style': 'dashed',
              'line-opacity': 0.7
            }
          },
          {
            selector: 'edge[edgeType="cross"]',
            style: { 
              'line-color': '#f59e0b', 
              'target-arrow-color': '#f59e0b', 
              'width': 2.5,
              'line-opacity': 1
            }
          },
          {
            selector: 'edge',
            style: {
              'target-arrow-shape': 'triangle',
              'curve-style': 'bezier',
              'arrow-scale': 1,
              'label': 'data(label)',
              'font-size': '7px',
              'color': '#94a3b8',
              'text-background-color': '#0f172a',
              'text-background-opacity': 0.9,
              'text-background-padding': '2px'
            }
          },
          {
            selector: ':selected',
            style: {
              'background-color': '#ef4444',
              'border-color': '#f87171',
              'border-width': 4
            }
          },
          {
            selector: '.highlighted',
            style: {
              'background-color': '#ef4444',
              'border-color': '#f87171',
              'border-width': 4,
              'z-index': 999
            }
          },
          {
            selector: '.dimmed',
            style: { 
              'opacity': 0.08,
              'z-index': 1
            }
          }
        ],
        layout: {
          name: 'cose',
          padding: 50,
          nodeRepulsion: 400000,
          edgeElasticity: 100,
          nestingFactor: 5,
          gravity: 80,
          numIter: 1000,
          initialTemp: 200,
          coolingFactor: 0.95,
          minTemp: 1.0,
          componentSpacing: 120,
          idealEdgeLength: 100
        },
        minZoom: 0.05,
        maxZoom: 5,
        wheelSensitivity: 0.2
      });

      console.log('Cytoscape ready, nodes:', cyInstance.current.nodes().length);

      // Fit after layout settles
      setTimeout(() => {
        if (cyInstance.current) {
          cyInstance.current.fit();
          cyInstance.current.center();
          console.log('Fit done, zoom:', cyInstance.current.zoom());
        }
      }, 1200);

      // Tooltip
      const tooltip = document.getElementById('cy-tooltip');
      if (tooltip) {
        cyInstance.current.on('mouseover', 'node', (evt: any) => {
          const n = evt.target;
          tooltip.innerHTML = `
            <div style="font-weight:600;color:#f8fafc;margin-bottom:4px">${n.data('label')}</div>
            <div style="color:#cbd5e1;font-size:11px;line-height:1.4">${(n.data('fullTitle') || '').substring(0, 250)}</div>
            <div style="color:#94a3b8;font-size:10px;margin-top:4px">База: ${n.data('base') === 'crm' ? 'CRM' : 'Actual'} | ID: ${n.id().substring(0, 20)}</div>
          `;
          tooltip.style.display = 'block';
        });
        cyInstance.current.on('mousemove', (evt: any) => {
          tooltip.style.left = (evt.originalEvent.pageX + 15) + 'px';
          tooltip.style.top = (evt.originalEvent.pageY + 15) + 'px';
        });
        cyInstance.current.on('mouseout', 'node', () => {
          tooltip.style.display = 'none';
        });
      }

      // Click — highlight neighborhood
      cyInstance.current.on('tap', 'node', (evt: any) => {
        const n = evt.target;
        const hood = n.neighborhood().add(n);
        cyInstance.current!.elements().not(hood).addClass('dimmed');
        hood.removeClass('dimmed');
        hood.addClass('highlighted');
      });
      cyInstance.current.on('tap', (evt: any) => {
        if (evt.target === cyInstance.current) {
          cyInstance.current!.elements().removeClass('dimmed highlighted');
        }
      });

    } catch (err) {
      console.error('Cytoscape init error:', err);
      setError('Ошибка инициализации графа: ' + (err as Error).message);
    }
  };

  const loadAll = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${API_URL}/api/graph/connections?limit=1000`);
      const all = [
        ...(res.data.crmLinks || []),
        ...(res.data.actualLinks || []),
        ...(res.data.crossLinks || [])
      ];
      
      setStats({
        crm: res.data.crmLinks?.length || 0,
        actual: res.data.actualLinks?.length || 0,
        cross: res.data.crossLinks?.length || 0,
        nodes: new Set(all.map((l: any) => l.s).concat(all.map((l: any) => l.t))).size,
        totalLinks: all.length
      });

      if (all.length === 0) {
        setError('Нет связей. Сначала выполните SQL для создания связей в базе.');
      } else {
        initCy(buildElements(all));
      }
    } catch (err: any) {
      setError('Ошибка загрузки: ' + (err.response?.data?.message || err.message));
    } finally {
      setLoading(false);
    }
  };

  const loadEgo = async () => {
    if (!searchId.trim()) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/graph/ego/${searchId}?depth=2`);
      const edges = res.data.data?.edges || [];
      initCy(buildElements(edges));
    } catch (err: any) {
      setError(err.response?.data?.message || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ width: '100vw', height: '100vh', background: '#0f172a', position: 'relative', overflow: 'hidden' }}>
      {/* Graph Container */}
      <div 
        ref={cyRef} 
        style={{ 
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%', 
          height: '100%',
          zIndex: 1
        }} 
      />

      {/* Header */}
      <div style={{
        position: 'absolute', top: 0, left: 0, right: 0, 
        background: 'rgba(15, 23, 42, 0.85)', 
        backdropFilter: 'blur(10px)',
        borderBottom: '1px solid #334155',
        padding: '10px 20px', 
        zIndex: 10,
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between'
      }}>
        <span style={{ color: '#f8fafc', fontSize: '16px', fontWeight: 600 }}>🔗 Граф связей</span>
        <span style={{ color: '#94a3b8', fontSize: '12px' }}>
          <span style={{ color: '#3b82f6' }}>● CRM: {stats.crm}</span>
          {' '}
          <span style={{ color: '#10b981' }}>● Actual: {stats.actual}</span>
          {' '}
          <span style={{ color: '#f59e0b' }}>● Cross: {stats.cross}</span>
          {' '}
          <span style={{ color: '#94a3b8' }}>Узлов: {stats.nodes} | Связей: {stats.totalLinks}</span>
        </span>
      </div>

      {/* Search */}
      <div style={{ 
        position: 'absolute', 
        top: '55px', 
        left: '20px', 
        zIndex: 10, 
        display: 'flex', 
        gap: '5px' 
      }}>
        <input
          type="text"
          value={searchId}
          onChange={e => setSearchId(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && loadEgo()}
          placeholder="ID документа..."
          style={{ 
            padding: '6px 12px', fontSize: '12px', width: '220px', 
            borderRadius: '6px', border: '1px solid #475569', 
            background: 'rgba(30, 41, 59, 0.9)', 
            color: '#e2e8f0', 
            outline: 'none'
          }}
        />
        <button onClick={loadEgo} style={{ 
          padding: '6px 12px', fontSize: '12px', background: '#3b82f6', 
          color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer'
        }}>🔍 Ego</button>
        <button onClick={loadAll} style={{ 
          padding: '6px 12px', fontSize: '12px', background: '#475569', 
          color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer'
        }}>📊 Все</button>
      </div>

      {/* Error */}
      {error && (
        <div style={{
          position: 'absolute', top: '100px', left: '20px', right: '20px',
          background: '#ef4444', color: '#fff', padding: '12px', 
          borderRadius: '8px', zIndex: 10
        }}>
          ⚠️ {error}
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div style={{
          position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
          color: '#94a3b8', zIndex: 10
        }}>
          Загрузка графа...
        </div>
      )}

      {/* Tooltip */}
      <div id="cy-tooltip" style={{
        position: 'fixed', background: 'rgba(15, 23, 42, 0.95)', color: '#fff',
        padding: '10px 14px', borderRadius: '8px', fontSize: '12px',
        pointerEvents: 'none', display: 'none', zIndex: 1000,
        maxWidth: '400px', border: '1px solid #475569',
        lineHeight: 1.5
      }} />

      {/* Legend */}
      <div style={{
        position: 'absolute', bottom: '15px', left: '15px',
        background: 'rgba(15, 23, 42, 0.9)', border: '1px solid #475569',
        padding: '10px 14px', borderRadius: '8px', fontSize: '12px', zIndex: 10
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
          <div style={{ width: 12, height: 12, borderRadius: '50%', background: '#3b82f6' }} />
          <span style={{ color: '#e2e8f0' }}>CRM (крупные)</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
          <div style={{ width: 12, height: 12, borderRadius: '50%', background: '#10b981' }} />
          <span style={{ color: '#e2e8f0' }}>Actual (мелкие)</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <div style={{ width: 12, height: 12, borderRadius: '50%', background: '#f59e0b' }} />
          <span style={{ color: '#e2e8f0' }}>Cross связь</span>
        </div>
      </div>

      {/* Controls */}
      <div style={{
        position: 'absolute', bottom: '15px', right: '15px', zIndex: 10,
        display: 'flex', gap: '6px'
      }}>
        <button onClick={() => cyInstance.current?.layout({ 
          name: 'cose', padding: 50, nodeRepulsion: 400000, gravity: 80, numIter: 1000 
        }).run()} style={{ 
          padding: '8px 14px', fontSize: '12px', background: '#1e293b', 
          color: '#e2e8f0', border: '1px solid #475569', borderRadius: '6px', cursor: 'pointer'
        }}>🔄 Перестроить</button>
        <button onClick={() => cyInstance.current?.fit()} style={{ 
          padding: '8px 14px', fontSize: '12px', background: '#1e293b', 
          color: '#e2e8f0', border: '1px solid #475569', borderRadius: '6px', cursor: 'pointer'
        }}>⛶ Вписать</button>
        <button onClick={() => cyInstance.current?.zoom(cyInstance.current.zoom() * 1.3)} style={{ 
          padding: '8px 14px', fontSize: '12px', background: '#1e293b', 
          color: '#e2e8f0', border: '1px solid #475569', borderRadius: '6px', cursor: 'pointer'
        }}>🔍+</button>
        <button onClick={() => cyInstance.current?.zoom(cyInstance.current.zoom() * 0.7)} style={{ 
          padding: '8px 14px', fontSize: '12px', background: '#1e293b', 
          color: '#e2e8f0', border: '1px solid #475569', borderRadius: '6px', cursor: 'pointer'
        }}>🔍-</button>
      </div>
    </div>
  );
};
